import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { PageStatus, Planogram } from "src/components";
import { useModals } from "src/components/Modals";
import { PlanogramHovererDetails } from "src/components/PlanogramExtensions/PlanogramHovererDetails/PlanogramHovererDetails";
import { Button, Color, Flex, Input, Modal, Text } from "src/elements";
import { Metrics } from "src/pages/MerchflowTemplates/components/Metrics";
import { useNavigation } from "src/utils/navigation";
import { pages } from "src/utils/pages";
import { storePlanogramReviewAtom } from "./store/atoms";
import { omniAtom } from "src/utils/atoms";
import { useApi } from "src/api";
import { useToasts } from "src/components/Toasts";
import { useStores_storeCodeData } from "src/pages/Merchflows_merchflowId_Stores_storeCode/hooks";
import { useLoaders } from "src/components/InlineLoader";
import { RoutePutMerchflows_MerchflowId_StoresApprovals } from "@CommonApi/merchflows/_merchflowId_/stores/approvals";
import { usePowerBiReportModal } from "../PowerBiReportModal/hooks";
import { useAsyncState } from "src/hooks/useAsyncState";
import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import { RoutePostMerchflowStorePog_storePogId_MarkDelivered } from "@CommonApi/merchflow/store/pog/_storePogId_/mark-delivered";
import { RoutePostMerchflowCreatePlanogramTemplate } from "@CommonApi/merchflow/createPlanogramTemplate";
import { OpenStorePlanogramReviewModal } from "./store/hooks";
import { routeToUrl } from "@CommonUtils/routes";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { hasPermission, PermissionCode } from "@CommonUtils/RolesPermissions";
import { useUser } from "src/atoms/user";
import { useStorePlanogramReviewStorePogQuery } from "./useStorePlanogramReviewStorePogQuery";
import { PreviewPdfSelector } from "./components/PreviewPdfSelector";
import { useTranslation } from "src/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { useConfirmDeliveryConflicts } from "src/hooks/useConfirmDeliveryConflicts";

export const StorePlanogramReviewModal = () => {
  const [data, setData] = useAtom(storePlanogramReviewAtom);

  useEffect(() => {
    return () => {
      setData(null);
    };
  }, []);

  return data && data.storePlanograms.length > 0 ? (
    <StorePlanogramReviewModalInner
      merchflowId={data.merchflowId}
      storePogId={data.storePogId}
      storePlanograms={data.storePlanograms}
    />
  ) : null;
};

const StorePlanogramReviewModalInner = ({
  merchflowId,
  storePogId,
  storePlanograms,
}: OpenStorePlanogramReviewModal) => {
  const {
    putMerchflowApprovalsApi,
    getMerchflowReviewApi,
    postMerchflowCreatePlanogramTemplateApi,
    postMarkStorePogDeliveredApi,
  } = useApi();
  const { t } = useTranslation("planogramDelivery");
  const { navigate } = useNavigation();
  const { closeModal: _closeModal, openConfirmModal } = useModals();
  const { hasLoader } = useLoaders();
  const { toast } = useToasts();
  const { openOneStoreReport, openCategoryRangeReviewReport } = usePowerBiReportModal();
  const { page, loadStorePogs } = useStores_storeCodeData();
  const { user } = useUser();
  const canCreate = hasPermission(user.roleCode, PermissionCode.MERCHFLOW_CREATE);
  const queryClient = useQueryClient();
  const confirmDeliveryConflicts = useConfirmDeliveryConflicts();

  const [data, setData] = useAtom(storePlanogramReviewAtom);
  const [planogramIndex, setPlanogramIndex] = useState(() =>
    Math.max(
      0,
      storePlanograms.findIndex((store) => store.storePog.storePogId === storePogId),
    ),
  );
  const [filter, setFilter] = useAsyncState<ResponseMerchFlowApplyFilters>(null);

  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [templateName, setTemplateName] = useState("");
  const isCreateTemplateLoading = hasLoader(RoutePostMerchflowCreatePlanogramTemplate);

  useEffect(() => {
    setFilter(getMerchflowReviewApi(merchflowId));
  }, []);

  const storePlanogramItems = data?.storePlanograms ?? storePlanograms;
  const planogramView = storePlanogramItems[planogramIndex] ?? storePlanogramItems[0];
  const storePogData = planogramView.storePog.storePogData;
  const currentStorePogId = planogramView.storePog.storePogId;

  const { data: storePogResponse, isLoading: isStorePogLoading } =
    useStorePlanogramReviewStorePogQuery(currentStorePogId, Boolean(storePogData));

  const storePlanogramAtom = useMemo(() => {
    return omniAtom({
      key: `modals/storePlanogramReview/storePlanogram/${planogramView.storePog.storePogId}`,
      value: storePogData ? new Pog(storePogData) : null,
    });
  }, [planogramView.storePog.storePogId, storePogData]);

  const isApproved = planogramView.storePog.isApproved;

  const loadedStorePog = storePogResponse?.pog;

  useEffect(() => {
    if (storePogData || !loadedStorePog) return;

    setData((currentData) => {
      if (!currentData) return null;

      return {
        ...currentData,
        storePlanograms: currentData.storePlanograms.map((storePlanogram) =>
          storePlanogram.storePog.storePogId === currentStorePogId
            ? {
                ...storePlanogram,
                templateId: storePogResponse.template_id,
                storeCode: storePogResponse.store,
                basePogId: storePogResponse.base_pog_id,
                storePog: {
                  ...storePlanogram.storePog,
                  storePogData: loadedStorePog,
                  isApproved: storePogResponse.is_approved,
                },
              }
            : storePlanogram,
        ),
      };
    });
  }, [currentStorePogId, loadedStorePog, setData, storePogData, storePogResponse]);

  const modalTitle = `Store Code: ${planogramView.storeCode}, Store Planogram ID: ${planogramView.storePog.storePogId}, Base Planogram ID: ${planogramView.basePogId}, Template ID: ${planogramView.templateId}`;

  const toggleStorePlanogramApprove = async () => {
    const approval = { store_pog_id: planogramView.storePog.storePogId };
    const response = await putMerchflowApprovalsApi(
      merchflowId,
      isApproved ? { unapproved: [approval] } : { approved: [approval] },
    );

    // Manually override data to update approval status.
    if (response) {
      setData((data) => {
        return data
          ? {
              ...data,
              storePlanograms: data.storePlanograms.map((storePlanogram, index) => ({
                ...storePlanogram,
                storePog: {
                  ...storePlanogram.storePog,
                  isApproved:
                    index === planogramIndex ? !isApproved : storePlanogram.storePog.isApproved,
                },
              })),
            }
          : null;
      });
      loadStorePogs(page);
    }
  };

  const closeModal = () => {
    _closeModal("StorePlanogramReviewModal");
  };

  const navigateToBasePogPage = () => {
    navigate(
      routeToUrl(pages.merchflows_merchflowId_BasePog_basePogId_, {
        merchflowId,
        basePogId: planogramView.basePogId,
      }),
    );
    closeModal();
  };

  const openCreateTemplatePlanogram = () => {
    if (!filter) return;

    setCategory(
      filter.filter_config.stores.find((store) => store.store === planogramView.storeCode)
        ?.retailer_category_code || "",
    );
    setSubCategory(
      filter.filter_config.stores.find((store) => store.store === planogramView.storeCode)
        ?.category_code || "",
    );
    setTemplateName(
      `STORE ID: ${planogramView.storeCode} - STORE POG ID: ${planogramView.storePog.storePogId}`,
    );
    setIsCreatingTemplate(true);
  };

  const closeCreateTemplatePlanogram = () => {
    setCategory("");
    setSubCategory("");
    setTemplateName("");
    setIsCreatingTemplate(false);
  };

  const navigateToEditStorePog = () => {
    if (!planogramView.storePog) return;

    navigate(
      routeToUrl(pages.merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_, {
        merchflowId,
        storeCode: planogramView.storeCode,
        storePogId: planogramView.storePog.storePogId,
      }),
    );
    closeModal();
  };

  const markStorePogAsDelivered = async () => {
    const deliveredStorePogId = planogramView.storePog.storePogId;
    await confirmDeliveryConflicts({ storePogId: deliveredStorePogId }, async () => {
      const response = await postMarkStorePogDeliveredApi(deliveredStorePogId);
      if (!response) return;

      void queryClient.invalidateQueries({ queryKey: ["space-planning"] });
      toast({
        title:
          response.status === "delivered"
            ? t("markDelivered.toastDelivered")
            : t("markDelivered.toastAlreadyDelivered"),
      });
    });
  };

  const navigateToTemplatePage = () => {
    if (!planogramView.templateId) return;

    navigate(
      routeToUrl(pages.merchflowTemplateBasePogs, {
        merchflowId,
        templateId: planogramView.templateId,
      }),
    );
    closeModal();
  };

  const openOneStoreIntegrityReport = async () => {
    if (!filter) return;

    openOneStoreReport([
      {
        table: "gen_id_table",
        column: "filter_config_id",
        value: [filter.id],
      },
      {
        table: "base_pog_id",
        column: "base_pog_id",
        value: [planogramView.basePogId],
      },
      {
        table: "store",
        column: "store_code",
        value: [planogramView.storeCode],
      },
    ]);
  };

  const openOneStoreCategoryRangeReviewReport = async () => {
    if (planogramView.projectId) {
      openCategoryRangeReviewReport(planogramView.projectId);
    }
  };

  const onPrev = () => {
    if (planogramIndex > 0) {
      setPlanogramIndex(planogramIndex - 1);
    }
  };

  const onNext = () => {
    if (planogramIndex < storePlanogramItems.length - 1) {
      setPlanogramIndex(planogramIndex + 1);
    }
  };

  const saveTemplatePlanogram = async () => {
    if (!planogramView.storePog) return;
    const success = await postMerchflowCreatePlanogramTemplateApi({
      store_pog_id: planogramView.storePog.storePogId,
      category: category,
      subcategory: subCategory,
      template_name: templateName,
    });

    if (!success) return;
    openConfirmModal({
      title: "Template Planogram saved successfully!",
      description: (
        <Text textAlign="center" variant="small2" color={Color.textSecondary}>
          To create a new merchflow using this template, go to Homepage and click{" "}
          <b>"Create from Template"</b>
        </Text>
      ),
      buttons: [
        {
          id: "ok",
          label: "OK",
          onClick: () => {
            setIsCreatingTemplate(false);
          },
        },
      ],
      icon: { name: "toastSuccess" },
    });
  };

  return (
    <Modal name="StorePlanogramReviewModal" title={modalTitle} width="90vw" height="95vh">
      <Flex height="100%" padding="10px" width="100%" justify="between" gap="10px">
        <Flex direction="column" minWidth="0" flexGrow={1}>
          {storePogData ? (
            <Planogram
              // Each store's plan opens fitted, not at the zoom the last one was left at.
              key={currentStorePogId}
              pogAtom={storePlanogramAtom}
              isPog
              isEditable
              allowKonvaEditor
              openFitted
            />
          ) : (
            <PageStatus
              label={isStorePogLoading ? "Loading planogram..." : "Planogram unavailable"}
              icon={isStorePogLoading ? "spinner" : "warningCircle"}
            />
          )}

          <PlanogramHovererDetails />
        </Flex>

        <Flex minWidth="210px" column justify="between" height="100%" gap="10px">
          <PreviewPdfSelector storePogId={currentStorePogId} isDisabled={!storePogData} />

          <Flex width="100%">
            <Button
              width="100%"
              dropdown={[
                {
                  label: isApproved ? "Unapprove" : "Approve",
                  onClick: toggleStorePlanogramApprove,
                },
                {
                  label: t("markDelivered.action"),
                  isDisabled: !isApproved,
                  tooltip: !isApproved ? t("markDelivered.approveFirstTooltip") : undefined,
                  isLoading: hasLoader(RoutePostMerchflowStorePog_storePogId_MarkDelivered),
                  onClick: markStorePogAsDelivered,
                },
                ...(canCreate
                  ? [
                      {
                        label: "Edit Template Planogram",
                        onClick: navigateToBasePogPage,
                      },
                      {
                        label: "Create Template Planogram",
                        onClick: openCreateTemplatePlanogram,
                        isLoading: !filter,
                      },
                    ]
                  : []),
                {
                  label: "Edit Store Planogram",
                  onClick: navigateToEditStorePog,
                },
                {
                  label: "View All Template versions",
                  onClick: navigateToTemplatePage,
                },
                {
                  label: "Reports",
                  isLoading: !filter,
                  buttons: [
                    {
                      label: "Store Integrity",
                      onClick: openOneStoreIntegrityReport,
                    },
                    {
                      label: "Category Range",
                      onClick: openOneStoreCategoryRangeReviewReport,
                      isDisabled:
                        planogramView.projectId === null || planogramView.projectId === undefined,
                    },
                  ],
                },
              ]}
              variant="inverted"
              isDisabled={!storePogData}
              isLoading={hasLoader(RoutePutMerchflows_MerchflowId_StoresApprovals)}
            >
              Store Options
            </Button>
          </Flex>

          <Metrics metrics={storePogData?.metrics} />

          {storePlanogramItems.length > 1 && (
            <Flex justify="space-between">
              <Button
                onClick={onPrev}
                variant="borderless"
                isDisabled={planogramIndex === 0}
                iconLeft={{ name: "arrowLeft" }}
              >
                Prev
              </Button>

              <Button
                onClick={onNext}
                variant="borderless"
                isDisabled={planogramIndex === storePlanogramItems.length - 1}
                iconRight={{ name: "arrowRight" }}
              >
                Next
              </Button>
            </Flex>
          )}

          {canCreate && isCreatingTemplate && (
            <Flex
              column
              gap="15px"
              background={Color.lightGray}
              borderColor={Color.lightGrayHover}
              borderRadius="3px"
              padding="5px"
              margin="20px 0 0 0"
            >
              <Text variant="body1">Create Template Planogram</Text>

              <Input title="Category" value={category} setValue={setCategory} isDisabled />

              <Input
                title="Sub-Category"
                value={subCategory}
                setValue={setSubCategory}
                isDisabled
              />

              <Input
                title="Template Name"
                value={templateName}
                setValue={setTemplateName}
                isDisabled={isCreateTemplateLoading}
              />

              <Flex justify="between">
                <Button
                  color="red"
                  variant="inverted"
                  onClick={closeCreateTemplatePlanogram}
                  isDisabled={isCreateTemplateLoading}
                >
                  Cancel
                </Button>

                <Button
                  color="greenSmoke"
                  onClick={saveTemplatePlanogram}
                  isLoading={isCreateTemplateLoading}
                >
                  Save
                </Button>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};
